import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarRange, X } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYMD = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmt = (s) => {
  const d = parseYMD(s);
  return d ? d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
};

function presetRange(key) {
  const now = new Date();
  const end = toYMD(now);
  const start = new Date(now);
  if (key === "yesterday") { start.setDate(now.getDate() - 1); const y = toYMD(start); return { from: y, to: y }; }
  if (key === "today") return { from: end, to: end };
  if (key === "tomorrow") { start.setDate(now.getDate() + 1); const t = toYMD(start); return { from: t, to: t }; }
  if (key === "7d") { start.setDate(now.getDate() - 6); return { from: toYMD(start), to: end }; }
  if (key === "30d") { start.setDate(now.getDate() - 29); return { from: toYMD(start), to: end }; }
  if (key === "month") return { from: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), to: end };
  return { from: "", to: "" }; // all time
}

const PRESETS = [
  ["yesterday", "Yesterday"], ["today", "Today"], ["tomorrow", "Tomorrow"],
  ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["month", "This month"], ["all", "All time"],
];

export default function DateRangePicker({ from, to, onChange, testid = "date-range", future = false }) {
  const [open, setOpen] = useState(false);
  // Future-looking presets (Tomorrow) only make sense for forward-dated filters
  // like Activities & Meetings — hidden for Leads/Reports/Dashboard/Recycle Bin.
  const presetList = future ? PRESETS : PRESETS.filter(([k]) => k !== "tomorrow");
  // Draft selection — committed only on Apply, so picking a date doesn't reload data.
  const [draft, setDraft] = useState({ from: parseYMD(from), to: parseYMD(to) });

  // Re-sync the draft to the applied range whenever the popover opens.
  useEffect(() => {
    if (open) setDraft({ from: parseYMD(from), to: parseYMD(to) });
  }, [open, from, to]);

  const hasApplied = !!from || !!to;
  const appliedLabel = hasApplied ? `${fmt(from) || "…"} – ${fmt(to) || "…"}` : "All time";
  const draftLabel = draft.from
    ? `${draft.from.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${draft.to ? draft.to.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "…"}`
    : "Select a date range";

  const apply = () => {
    onChange({
      created_from: draft.from ? toYMD(draft.from) : "",
      created_to: draft.to ? toYMD(draft.to) : (draft.from ? toYMD(draft.from) : ""),
    });
    setOpen(false);
  };
  const clear = () => {
    setDraft({ from: undefined, to: undefined });
    onChange({ created_from: "", created_to: "" });
    setOpen(false);
  };
  const applyPreset = (key) => {
    if (key === "all") { clear(); return; }
    const r = presetRange(key);
    onChange({ created_from: r.from, created_to: r.to });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testid}-trigger`}
          className="flex h-9 items-center gap-2 rounded-lg border border-sky bg-white px-3 text-sm font-medium text-navy outline-none transition-colors hover:border-loop focus:border-loop focus:ring-2 focus:ring-loop/20"
        >
          <CalendarRange className="h-4 w-4 text-loop" />
          <span className={hasApplied ? "text-navy" : "text-ink/50"}>{appliedLabel}</span>
          {hasApplied && (
            <X
              role="button"
              data-testid={`${testid}-clear`}
              onClick={(e) => { e.stopPropagation(); clear(); }}
              className="h-3.5 w-3.5 rounded-full text-ink/40 hover:bg-mist hover:text-red-500"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[95vw] overflow-x-auto border-sky p-0" align="end" collisionPadding={12}>
        <div className="flex">
          <div className="flex shrink-0 flex-col gap-1 border-r border-sky/60 p-3">
            {presetList.map(([key, lbl]) => (
              <button
                key={key}
                type="button"
                data-testid={`${testid}-preset-${key}`}
                onClick={() => applyPreset(key)}
                className="rounded-lg px-3 py-1.5 text-left text-xs font-medium text-ink/70 hover:bg-sky/40 hover:text-loop"
              >
                {lbl}
              </button>
            ))}
          </div>
          <div>
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={draft.from ? draft : undefined}
              onSelect={(range) => setDraft(range || { from: undefined, to: undefined })}
              initialFocus
            />
            <div className="flex items-center justify-between gap-2 border-t border-sky/60 px-3 py-2.5">
              <span className="text-xs text-ink/55" data-testid={`${testid}-draft-label`}>{draftLabel}</span>
              <div className="flex items-center gap-1.5">
                <button type="button" data-testid={`${testid}-cancel`} onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist">Cancel</button>
                <button type="button" data-testid={`${testid}-clear-action`} onClick={clear} className="rounded-lg px-3 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist">Clear</button>
                <button type="button" data-testid={`${testid}-apply`} onClick={apply} disabled={draft.from && !draft.to} className="rounded-lg bg-loop px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-loophover disabled:opacity-50">Apply</button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
